import type { Component } from 'vue'
import { LockClosedIcon, NoSymbolIcon } from '@heroicons/vue/24/outline'

type SwitchDialogType = 'confirm-switch' | 'require-switch'
type SwitchDialogState = 'prompt' | 'processing' | 'success'
export interface Content {
  icon?: Component
  header?: string
  description?: string | Component
}

export const usePermissionsDialogStore = defineStore('permissions-dialog', () => {
  const type = ref<SwitchDialogType>()
  const state = ref<SwitchDialogState>('prompt')
  const forbiddenModal = ref<Content & { isOpen: boolean }>({ isOpen: false })
  const isSwitchModalOpen = ref(false)

  const isForbiddenModalOpen = computed(() => forbiddenModal.value.isOpen)

  function showConfirmSwitch() {
    type.value = 'confirm-switch'
    isSwitchModalOpen.value = true
  }

  function showRequireSwitch() {
    type.value = 'require-switch'
    isSwitchModalOpen.value = true
  }

  function showForbiddenTeammateAction(content?: Content) {
    forbiddenModal.value = {
      ...content,
      isOpen: true,
    }
  }

  function showForbiddenRfqSubmission(isResearch: boolean, projectTicker?: string) {
    const content: Content = isResearch
      ? {
          icon: NoSymbolIcon,
          header: 'You cannot submit an RFQ.',
          description: 'You are visiting Forgd as a researcher. A regular account is needed to submit an RFQ.',
        }
      : {
          icon: LockClosedIcon,
          header: 'This action can only be performed by the user with token admin role',
          description: () => h('span', [
            'Your role on ',
            h('span', { class: 'font-semibold' }, projectTicker ?? 'this token'),
            ' is teammate. Teammates have read-only access at the moment on playbook activities and tools. If you would like to perform modifications please contact the admin of the token directly.',
          ]),
        }

    forbiddenModal.value = {
      ...content,
      isOpen: true,
    }
  }

  async function closeSwitchModal() {
    isSwitchModalOpen.value = false

    // INFO: Prevents flashing an empty state
    await new Promise(resolve => setTimeout(resolve, 200))
    state.value = 'prompt'
    type.value = undefined
    resetTextContent()
  }

  function resetTextContent() {
    forbiddenModal.value = {
      isOpen: false,
    }
  }

  async function closeForbiddenModal() {
    forbiddenModal.value.isOpen = false

    // INFO: Prevents flashing an empty state
    await new Promise(resolve => setTimeout(resolve, 200))
    resetTextContent()
  }

  return {
    state,
    type,
    forbiddenModal,
    isSwitchModalOpen,
    isForbiddenModalOpen,
    closeForbiddenModal,
    closeSwitchModal,
    showConfirmSwitch,
    showRequireSwitch,
    showForbiddenTeammateAction,
    showForbiddenRfqSubmission,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(usePermissionsDialogStore, import.meta.hot))
}
